import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import authFetch from '../../utils/authFetch';
import { 
    DocumentTextIcon, 
    EyeIcon, 
    SparklesIcon,
    CheckCircleIcon,
    ArrowRightIcon,
    UserIcon,
    BriefcaseIcon,
    AcademicCapIcon,
    WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';
import TemplateSelector from './TemplateSelector';
import CvPreview from './CvPreview';
import SpellCheckInput from '../SpellCheckInput';
import { trackCvGeneration, trackCvExport } from '../../utils/analytics';
import { exportCvToPdf, downloadBlob } from '../../utils/pdfExport';
import { generateOptimizedCvHtml, compressHtml } from '../../utils/optimizedCvGenerator';
import { generateRealPDF } from '../../utils/pdfGenerator';
import { useToast } from '../ToastProvider';

const CvGeneratorSimplified = () => {
    const { success, error: toastError, info } = useToast();
    const [currentStep, setCurrentStep] = useState('template'); // template, quick-edit, preview
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [cvData, setCvData] = useState({
        personalInfo: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
            linkedin: '',
            website: ''
        },
        summary: '',
        experience: [],
        education: [],
        skills: [],
        languages: [],
        certifications: [],
        projects: []
    });
    const [cvStyle, setCvStyle] = useState({
        primaryColor: '#2563eb',
        secondaryColor: '#1e40af',
        fontFamily: 'Inter',
        fontSize: 'medium',
        spacing: 'normal'
    });
    const [isLoading, setIsLoading] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const saveTimeoutRef = useRef(null);

    const token = useSelector((state) => state.auth.token);
    const userId = useSelector((state) => state.auth.user.id);

    useEffect(() => {
        // Tenter de restaurer un brouillon local si présent
        try {
            const draftKey = `cv_draft_simplified_${userId || 'guest'}`;
            const draft = localStorage.getItem(draftKey);
            if (draft) {
                const { cvData: dData, cvStyle: dStyle, template: dTemplate, currentStep: dStep } = JSON.parse(draft);
                if (dData) setCvData(dData);
                if (dStyle) setCvStyle(dStyle);
                if (dTemplate) setSelectedTemplate(dTemplate);
                if (dStep && dStep !== 'template') setCurrentStep(dStep);
                info('Brouillon local simplifié restauré');
            } else {
                // Si pas de brouillon, essayer de récupérer les données du profil utilisateur
                loadUserProfileData();
            }
        } catch (error) {
            // Erreur silencieuse pour la restauration du brouillon
            loadUserProfileData();
        }

        const beforeUnload = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => {
            window.removeEventListener('beforeunload', beforeUnload);
            // Nettoyer le timeout de sauvegarde
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, info]); // Retiré hasUnsavedChanges des dépendances

    const loadUserProfileData = async () => {
        if (!token || !userId || dataLoaded) return;

        try {
            // Récupérer les données CV existantes de l'utilisateur
            const response = await authFetch(`https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/user-data/${userId}`);

            if (response.ok) {
                const data = await response.json();
                if (data.cvData) {
                    // Utiliser directement les données CV existantes
                    setCvData(data.cvData);
                    
                    // Appliquer le style s'il existe
                    if (data.cvStyle) {
                        setCvStyle(data.cvStyle);
                    }
                    
                    // Appliquer le template s'il existe
                    if (data.template) {
                        setSelectedTemplate(data.template);
                    }

                    info('Données du profil utilisateur chargées');
                }
            }
            setDataLoaded(true);
        } catch (error) {
            console.log('Aucun CV existant trouvé, démarrage avec des données vides');
            setDataLoaded(true);
            // Optionnel : afficher un message informatif
            // info('Aucune donnée existante trouvée, création d\'un nouveau CV');
        }
    };

    const templates = [
        {
            id: 'modern-blue',
            name: 'Moderne Bleu',
            preview: '/templates/modern-blue.png',
            colors: { primary: '#2563eb', secondary: '#1e40af' },
            font: 'Inter',
            category: 'moderne'
        },
        {
            id: 'elegant-dark',
            name: 'Élégant Sombre',
            preview: '/templates/elegant-dark.png',
            colors: { primary: '#1f2937', secondary: '#374151' },
            font: 'Poppins',
            category: 'élégant'
        },
        {
            id: 'minimal-clean',
            name: 'Minimal Propre',
            preview: '/templates/minimal-clean.png',
            colors: { primary: '#059669', secondary: '#047857' },
            font: 'Roboto',
            category: 'minimal'
        },
        {
            id: 'creative-purple',
            name: 'Créatif Violet',
            preview: '/templates/creative-purple.png',
            colors: { primary: '#7c3aed', secondary: '#5b21b6' },
            font: 'Montserrat',
            category: 'créatif'
        },
        {
            id: 'professional-gray',
            name: 'Professionnel Gris',
            preview: '/templates/professional-gray.png',
            colors: { primary: '#4b5563', secondary: '#374151' },
            font: 'Open Sans',
            category: 'professionnel'
        },
        {
            id: 'modern-green',
            name: 'Moderne Vert',
            preview: '/templates/modern-green.png',
            colors: { primary: '#10b981', secondary: '#059669' },
            font: 'Inter',
            category: 'moderne'
        },
        {
            id: 'elegant-red',
            name: 'Élégant Rouge',
            preview: '/templates/elegant-red.png',
            colors: { primary: '#dc2626', secondary: '#b91c1c' },
            font: 'Playfair Display',
            category: 'élégant'
        },
        {
            id: 'minimal-blue',
            name: 'Minimal Bleu',
            preview: '/templates/minimal-blue.png',
            colors: { primary: '#0ea5e9', secondary: '#0284c7' },
            font: 'Roboto',
            category: 'minimal'
        }
    ];

    const saveLocalDraft = (cvData, cvStyle, template, step) => {
        try {
            const draftKey = `cv_draft_simplified_${userId || 'guest'}`;
            const draft = {
                cvData,
                cvStyle,
                template,
                currentStep: step,
                timestamp: Date.now()
            };
            localStorage.setItem(draftKey, JSON.stringify(draft));
        } catch (error) {
            console.warn('Impossible de sauvegarder le brouillon local:', error);
        }
    };

    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setCvStyle(prev => ({
            ...prev,
            primaryColor: template.colors.primary,
            secondaryColor: template.colors.secondary,
            fontFamily: template.font
        }));
        setCurrentStep('quick-edit');
        
        // Sauvegarder le brouillon local
        saveLocalDraft(cvData, cvStyle, template, 'quick-edit');
        
        // Tracking
        trackCvGeneration('template_selected', template.id);
    };

    const handleCvDataUpdate = (newData) => {
        setCvData(newData);
        setHasUnsavedChanges(true);
        
        // Sauvegarder le brouillon local
        saveLocalDraft(newData, cvStyle, selectedTemplate, currentStep);
        
        // Debouncing : sauvegarder après 2 secondes d'inactivité
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        
        saveTimeoutRef.current = setTimeout(() => {
            saveCvDataToServer(newData, cvStyle, selectedTemplate);
        }, 2000);
    };

    const handleDeleteExperience = (experienceId) => {
        const updatedExperience = cvData.experience.filter(exp => exp.id !== experienceId);
        handleCvDataUpdate({
            ...cvData,
            experience: updatedExperience
        });
    };

    const handleDeleteEducation = (educationId) => {
        const updatedEducation = cvData.education.filter(edu => edu.id !== educationId);
        handleCvDataUpdate({
            ...cvData,
            education: updatedEducation
        });
    };

    const handleDeleteSkill = (skillId) => {
        const updatedSkills = cvData.skills.filter(skill => skill.id !== skillId);
        handleCvDataUpdate({
            ...cvData,
            skills: updatedSkills
        });
    };

    const handlePreview = () => {
        setCurrentStep('preview');
        // Sauvegarder le brouillon local avec l'étape preview
        saveLocalDraft(cvData, cvStyle, selectedTemplate, 'preview');
    };

    const handleLoadExistingData = async () => {
        setIsLoading(true);
        setDataLoaded(false); // Réinitialiser pour permettre un nouveau chargement
        try {
            await loadUserProfileData();
            success('Données du profil chargées avec succès');
        } catch (error) {
            toastError('Erreur lors du chargement des données existantes');
        } finally {
            setIsLoading(false);
        }
    };


    const saveCvDataToServer = async (cvData, cvStyle, template) => {
        if (!token || !userId || isSaving) return;

        setIsSaving(true);
        try {
            const response = await authFetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/save-data', {
                method: 'POST',
                body: JSON.stringify({
                    userId,
                    cvData,
                    cvStyle,
                    template,
                    mode: 'simplified'
                })
            });

            if (response.ok) {
                console.log('Données CV sauvegardées sur le serveur');
                setHasUnsavedChanges(false);
            } else {
                console.warn('Erreur lors de la sauvegarde sur le serveur');
            }
        } catch (error) {
            console.warn('Erreur de sauvegarde:', error);
            // Ne pas afficher d'erreur à l'utilisateur pour la sauvegarde automatique
        } finally {
            setIsSaving(false);
        }
    };

    const generateCvHtml = (cvData, cvStyle, template) => {
        // Utiliser le générateur optimisé
        const html = generateOptimizedCvHtml(cvData, cvStyle, template);
        return compressHtml(html);
    };

    const validateCvData = () => {
        const errors = [];
        
        if (!cvData.personalInfo.firstName) errors.push('Prénom manquant');
        if (!cvData.personalInfo.lastName) errors.push('Nom manquant');
        if (!cvData.personalInfo.email) errors.push('Email manquant');
        if (!cvData.summary || cvData.summary.length < 10) errors.push('Résumé trop court');
        if (!selectedTemplate) errors.push('Template non sélectionné');
        
        return errors;
    };

    const handleExport = async (format = 'pdf') => {
        // Validation des données essentielles avant export
        const validationErrors = validateCvData();
        if (validationErrors.length > 0) {
            toastError(`Données essentielles manquantes : ${validationErrors.join(', ')}`);
            return;
        }

        setIsLoading(true);
        try {
            if (format === 'pdf') {
                // Préparer les données pour l'API en filtrant les entrées vides
                const cleanExperience = (cvData.experience || []).filter(exp => 
                    exp.title && exp.title.trim() && exp.company && exp.company.trim()
                );
                
                const cleanEducation = (cvData.education || []).filter(edu => 
                    edu.degree && edu.degree.trim() && edu.institution && edu.institution.trim()
                );
                
                const cleanSkills = (cvData.skills || []).filter(skill => 
                    skill.name && skill.name.trim()
                );

                // Normaliser les compétences pour l'export
                const normalizedSkills = cleanSkills.map(skill => {
                    if (typeof skill === 'string') {
                        return { id: Date.now() + Math.random(), name: skill, level: 'intermediate' };
                    } else if (typeof skill === 'object' && skill !== null) {
                        // S'assurer que l'objet a la bonne structure
                        return {
                            id: skill.id || Date.now() + Math.random(),
                            name: skill.name || skill.title || skill.skill || skill.value || '[Compétence]',
                            level: skill.level || 'intermediate'
                        };
                    }
                    return skill;
                });

                // Debug des compétences normalisées
                console.log('Compétences normalisées pour export:', normalizedSkills);

                // Informer l'utilisateur si des éléments ont été filtrés
                const originalExpCount = (cvData.experience || []).length;
                const originalEduCount = (cvData.education || []).length;
                const originalSkillsCount = (cvData.skills || []).length;
                
                if (originalExpCount > cleanExperience.length || originalEduCount > cleanEducation.length || originalSkillsCount > normalizedSkills.length) {
                    const filteredItems = [];
                    if (originalExpCount > cleanExperience.length) {
                        filteredItems.push(`${originalExpCount - cleanExperience.length} expérience(s) incomplète(s)`);
                    }
                    if (originalEduCount > cleanEducation.length) {
                        filteredItems.push(`${originalEduCount - cleanEducation.length} formation(s) incomplète(s)`);
                    }
                    if (originalSkillsCount > normalizedSkills.length) {
                        filteredItems.push(`${originalSkillsCount - normalizedSkills.length} compétence(s) vide(s)`);
                    }
                    
                    info(`Export en cours... ${filteredItems.join(', ')} ont été automatiquement exclues.`);
                }

                const exportData = {
                    cvData: {
                        ...cvData,
                        // Filtrer les sections vides
                        experience: cleanExperience,
                        education: cleanEducation,
                        skills: normalizedSkills,
                        languages: cvData.languages || [],
                        certifications: cvData.certifications || [],
                        projects: cvData.projects || []
                    },
                    cvStyle,
                    template: selectedTemplate,
                    format: 'pdf'
                };

                console.log('Données envoyées pour export:', exportData);

                // Vérifier si les compétences sont correctement formatées
                const hasValidSkills = normalizedSkills.every(skill => 
                    skill && typeof skill === 'object' && skill.name && typeof skill.name === 'string' && skill.name.trim()
                );
                
                // Solution : utiliser directement le fallback local pour les compétences
                if (normalizedSkills.length > 0) {
                    console.log('Utilisation du fallback local pour les compétences');
                    
                    // Fallback : générer un vrai PDF
                    const fallbackCvData = {
                        ...cvData,
                        skills: normalizedSkills
                    };
                    
                    // Forcer l'utilisation du fallback local (pas l'API)
                    try {
                        const pdf = await generateRealPDF(fallbackCvData, cvStyle, selectedTemplate);
                        const filename = `CV_${cvData.personalInfo.firstName}_${cvData.personalInfo.lastName}.pdf`;
                        pdf.save(filename);
                        success('PDF exporté avec succès');
                    } catch (pdfError) {
                        console.error('Erreur génération PDF:', pdfError);
                        // Dernier recours : HTML pour impression
                        const cvHtml = generateCvHtml(fallbackCvData, cvStyle, selectedTemplate);
                        const printWindow = window.open('', '_blank');
                        printWindow.document.write(cvHtml);
                        printWindow.document.close();
                        printWindow.focus();
                        setTimeout(() => {
                            printWindow.print();
                            success('Fenêtre d\'impression ouverte - Utilisez "Enregistrer au format PDF"');
                        }, 500);
                    }
                    return; // Sortir de la fonction après le fallback
                }
                
                if (!hasValidSkills) {
                    console.warn('Compétences mal formatées, utilisation du fallback local');
                    // Même traitement que ci-dessus
                    const fallbackCvData = {
                        ...cvData,
                        skills: normalizedSkills
                    };
                    const cvHtml = generateCvHtml(fallbackCvData, cvStyle, selectedTemplate);
                    
                    // Créer un blob HTML et le télécharger
                    const blob = new Blob([cvHtml], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `CV_${cvData.personalInfo.firstName}_${cvData.personalInfo.lastName}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    
                    success('CV généré avec succès (fichier HTML téléchargeable)');
                    return;
                }

                // Si pas de compétences, essayer l'API normale
                try {
                    const blob = await exportCvToPdf(exportData.cvData, cvStyle, selectedTemplate, token);
                    if (blob) {
                        const filename = `CV_${cvData.personalInfo.firstName}_${cvData.personalInfo.lastName}.pdf`;
                        downloadBlob(blob, filename);
                        trackCvExport('pdf', selectedTemplate.id);
                        success('PDF exporté avec succès');
                    }
                } catch (apiError) {
                    console.warn('Export API échoué, utilisation du fallback local:', apiError);
                    
                    // Fallback : générer un fichier HTML téléchargeable
                    const fallbackCvData = {
                        ...cvData,
                        skills: normalizedSkills
                    };
                    const cvHtml = generateCvHtml(fallbackCvData, cvStyle, selectedTemplate);
                    
                    // Créer un blob HTML et le télécharger
                    const blob = new Blob([cvHtml], { type: 'application/pdf' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `CV_${cvData.personalInfo.firstName}_${cvData.personalInfo.lastName}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    URL.revokeObjectURL(url);
                    
                    success('CV généré avec succès (fichier HTML téléchargeable)');
                }
            } else if (format === 'docx') {
                // Export Word - même nettoyage des données
                const cleanExperience = (cvData.experience || []).filter(exp => 
                    exp.title && exp.title.trim() && exp.company && exp.company.trim()
                );
                
                const cleanEducation = (cvData.education || []).filter(edu => 
                    edu.degree && edu.degree.trim() && edu.institution && edu.institution.trim()
                );
                
                const cleanSkills = (cvData.skills || []).filter(skill => 
                    skill.name && skill.name.trim()
                );

                // Normaliser les compétences pour l'export Word
                const normalizedSkills = cleanSkills.map(skill => {
                    if (typeof skill === 'string') {
                        return { id: Date.now() + Math.random(), name: skill, level: 'intermediate' };
                    } else if (typeof skill === 'object' && skill !== null) {
                        // S'assurer que l'objet a la bonne structure
                        return {
                            id: skill.id || Date.now() + Math.random(),
                            name: skill.name || skill.title || skill.skill || skill.value || '[Compétence]',
                            level: skill.level || 'intermediate'
                        };
                    }
                    return skill;
                });

                // Debug des compétences normalisées pour Word
                console.log('Compétences normalisées pour export Word:', normalizedSkills);

                // Informer l'utilisateur si des éléments ont été filtrés
                const originalExpCount = (cvData.experience || []).length;
                const originalEduCount = (cvData.education || []).length;
                const originalSkillsCount = (cvData.skills || []).length;
                
                if (originalExpCount > cleanExperience.length || originalEduCount > cleanEducation.length || originalSkillsCount > normalizedSkills.length) {
                    const filteredItems = [];
                    if (originalExpCount > cleanExperience.length) {
                        filteredItems.push(`${originalExpCount - cleanExperience.length} expérience(s) incomplète(s)`);
                    }
                    if (originalEduCount > cleanEducation.length) {
                        filteredItems.push(`${originalEduCount - cleanEducation.length} formation(s) incomplète(s)`);
                    }
                    if (originalSkillsCount > normalizedSkills.length) {
                        filteredItems.push(`${originalSkillsCount - normalizedSkills.length} compétence(s) vide(s)`);
                    }
                    
                    info(`Export Word en cours... ${filteredItems.join(', ')} ont été automatiquement exclues.`);
                }

                const exportData = {
                    cvData: {
                        ...cvData,
                        experience: cleanExperience,
                        education: cleanEducation,
                        skills: normalizedSkills,
                        languages: cvData.languages || [],
                        certifications: cvData.certifications || [],
                        projects: cvData.projects || []
                    },
                    cvStyle,
                    template: selectedTemplate,
                    format: 'docx'
                };

                const response = await authFetch('https://cvanalyzerback.osc-fr1.scalingo.io/api/cv/export-word-from-data', {
                    method: 'POST',
                    body: JSON.stringify(exportData)
                });

                if (response.ok) {
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `CV_${cvData.personalInfo.firstName}_${cvData.personalInfo.lastName}.docx`;
                    document.body.appendChild(a);
                    a.click();
                    window.URL.revokeObjectURL(url);
                    document.body.removeChild(a);
                    
                    trackCvExport('docx', selectedTemplate.id);
                    success('Fichier Word exporté avec succès');
                } else {
                    const errorText = await response.text();
                    console.error('Erreur serveur:', errorText);
                    throw new Error(`Erreur ${response.status}: ${response.statusText}`);
                }
            }
        } catch (error) {
            console.error('Erreur détaillée:', error);
            toastError(`Erreur lors de l'export du CV: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const renderQuickEdit = () => (
        <div className="p-6">
            {/* Header */}
            <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                    Créez votre CV en 5 minutes
                </h2>
                <p className="text-gray-600 max-w-2xl mx-auto mb-4">
                    Remplissez les informations essentielles. Vous pourrez toujours les modifier plus tard.
                </p>
                
                {/* Bouton pour charger les données existantes */}
                <button
                    onClick={handleLoadExistingData}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                    {isLoading ? (
                        <>
                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Chargement...
                        </>
                    ) : (
                        <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                            Charger mes données existantes
                        </>
                    )}
                </button>
            </div>

            {/* Formulaire simplifié */}
            <div className="max-w-4xl mx-auto space-y-8">
                {/* Informations personnelles */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                            <UserIcon className="w-6 h-6 text-blue-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800">Informations personnelles</h3>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Prénom *</label>
                            <SpellCheckInput
                                value={cvData.personalInfo.firstName}
                                onChange={(value) => handleCvDataUpdate({
                                    ...cvData,
                                    personalInfo: { ...cvData.personalInfo, firstName: value }
                                })}
                                placeholder="Votre prénom"
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Nom *</label>
                            <SpellCheckInput
                                value={cvData.personalInfo.lastName}
                                onChange={(value) => handleCvDataUpdate({
                                    ...cvData,
                                    personalInfo: { ...cvData.personalInfo, lastName: value }
                                })}
                                placeholder="Votre nom"
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                            <input
                                type="email"
                                value={cvData.personalInfo.email}
                                onChange={(e) => handleCvDataUpdate({
                                    ...cvData,
                                    personalInfo: { ...cvData.personalInfo, email: e.target.value }
                                })}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="votre@email.com"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Téléphone</label>
                            <input
                                type="tel"
                                value={cvData.personalInfo.phone}
                                onChange={(e) => handleCvDataUpdate({
                                    ...cvData,
                                    personalInfo: { ...cvData.personalInfo, phone: e.target.value }
                                })}
                                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="06 12 34 56 78"
                            />
                        </div>
                    </div>
                </div>

                {/* Résumé professionnel */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                            <DocumentTextIcon className="w-6 h-6 text-green-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800">Résumé professionnel</h3>
                    </div>
                    
                    <SpellCheckInput
                        value={cvData.summary}
                        onChange={(value) => handleCvDataUpdate({
                            ...cvData,
                            summary: value
                        })}
                        rows={4}
                        spellCheck={true}
                        strictMode={false} // Mode permissif pour le résumé
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Décrivez brièvement votre profil professionnel (2-3 phrases)..."
                    />
                    <p className="text-sm text-gray-500 mt-2">
                        💡 Conseil : Mentionnez votre expérience principale et vos compétences clés
                    </p>
                </div>

                {/* Expérience professionnelle */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                            <BriefcaseIcon className="w-6 h-6 text-purple-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800">Expérience professionnelle</h3>
                    </div>
                    
                    <div className="space-y-4">
                        {cvData.experience.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <BriefcaseIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p>Aucune expérience ajoutée</p>
                                <p className="text-sm">Cliquez sur "Ajouter une expérience" pour commencer</p>
                            </div>
                        ) : (
                            cvData.experience.map((exp, index) => (
                                <div key={exp.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200 relative">
                                    {/* Bouton de suppression */}
                                    <button
                                        onClick={() => handleDeleteExperience(exp.id)}
                                        className="absolute top-3 right-3 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Supprimer cette expérience"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Poste</label>
                                            <SpellCheckInput
                                                value={exp.title}
                                                onChange={(value) => {
                                                    const newExp = { ...exp, title: value };
                                                    handleCvDataUpdate({
                                                        ...cvData,
                                                        experience: cvData.experience.map(e => e.id === exp.id ? newExp : e)
                                                    });
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Ex: Développeur Web"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Entreprise</label>
                                            <SpellCheckInput
                                                value={exp.company}
                                                onChange={(value) => {
                                                    const newExp = { ...exp, company: value };
                                                    handleCvDataUpdate({
                                                        ...cvData,
                                                        experience: cvData.experience.map(e => e.id === exp.id ? newExp : e)
                                                    });
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Ex: Google"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        
                        <button
                            onClick={() => {
                                const newExp = {
                                    id: Date.now(),
                                    title: '',
                                    company: '',
                                    location: '',
                                    startDate: '',
                                    endDate: '',
                                    current: false,
                                    description: ''
                                };
                                handleCvDataUpdate({
                                    ...cvData,
                                    experience: [...cvData.experience, newExp]
                                });
                            }}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                        >
                            <BriefcaseIcon className="w-5 h-5" />
                            Ajouter une expérience
                        </button>
                    </div>
                </div>

                {/* Formation */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                            <AcademicCapIcon className="w-6 h-6 text-green-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800">Formation</h3>
                    </div>
                    
                    <div className="space-y-4">
                        {cvData.education.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <AcademicCapIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p>Aucune formation ajoutée</p>
                                <p className="text-sm">Cliquez sur "Ajouter une formation" pour commencer</p>
                            </div>
                        ) : (
                            cvData.education.map((edu, index) => (
                                <div key={edu.id} className="bg-gray-50 rounded-xl p-4 border border-gray-200 relative">
                                    {/* Bouton de suppression */}
                                    <button
                                        onClick={() => handleDeleteEducation(edu.id)}
                                        className="absolute top-3 right-3 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Supprimer cette formation"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                    </button>
                                    
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Diplôme</label>
                                            <SpellCheckInput
                                                value={edu.degree}
                                                onChange={(value) => {
                                                    const newEdu = { ...edu, degree: value };
                                                    handleCvDataUpdate({
                                                        ...cvData,
                                                        education: cvData.education.map(e => e.id === edu.id ? newEdu : e)
                                                    });
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Ex: Master en Informatique"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Établissement</label>
                                            <SpellCheckInput
                                                value={edu.institution}
                                                onChange={(value) => {
                                                    const newEdu = { ...edu, institution: value };
                                                    handleCvDataUpdate({
                                                        ...cvData,
                                                        education: cvData.education.map(e => e.id === edu.id ? newEdu : e)
                                                    });
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Ex: Université de Paris"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        
                        <button
                            onClick={() => {
                                const newEdu = {
                                    id: Date.now(),
                                    degree: '',
                                    institution: '',
                                    location: '',
                                    startDate: '',
                                    endDate: '',
                                    current: false,
                                    description: ''
                                };
                                handleCvDataUpdate({
                                    ...cvData,
                                    education: [...cvData.education, newEdu]
                                });
                            }}
                            className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors"
                        >
                            + Ajouter une formation
                        </button>
                    </div>
                </div>

                {/* Compétences */}
                <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                            <WrenchScrewdriverIcon className="w-6 h-6 text-orange-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800">Compétences</h3>
                    </div>
                    
                    <div className="space-y-4">
                        {cvData.skills.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                                <WrenchScrewdriverIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                                <p>Aucune compétence ajoutée</p>
                                <p className="text-sm">Ajoutez vos compétences principales</p>
                            </div>
                        ) : (
                            <div className="flex flex-wrap gap-2">
                                {cvData.skills.map((skill) => (
                                    <span key={skill.id} className="flex items-center gap-2 px-3 py-2 bg-blue-100 text-blue-800 rounded-full text-sm">
                                        {skill.name}
                                        <button
                                            onClick={() => handleDeleteSkill(skill.id)}
                                            className="text-red-500 hover:text-red-700 hover:bg-red-100 rounded-full p-1 transition-colors"
                                            title="Supprimer cette compétence"
                                        >
                                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        
                        <div className="flex gap-2">
                            <input
                                type="text"
                                placeholder="Ex: JavaScript, Python, Leadership..."
                                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        const skillName = e.target.value.trim();
                                        if (skillName) {
                                            const newSkill = {
                                                id: Date.now(),
                                                name: skillName,
                                                level: 'intermediate'
                                            };
                                            handleCvDataUpdate({
                                                ...cvData,
                                                skills: [...cvData.skills, newSkill]
                                            });
                                            e.target.value = '';
                                        }
                                    }
                                }}
                            />
                            <button
                                onClick={() => {
                                    const input = document.querySelector('input[placeholder*="JavaScript"]');
                                    const skillName = input.value.trim();
                                    if (skillName) {
                                        const newSkill = {
                                            id: Date.now(),
                                            name: skillName,
                                            level: 'intermediate'
                                        };
                                        handleCvDataUpdate({
                                            ...cvData,
                                            skills: [...cvData.skills, newSkill]
                                        });
                                        input.value = '';
                                    }
                                }}
                                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                            >
                                Ajouter
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex justify-center gap-4 mt-8">
                <button
                    onClick={() => setCurrentStep('template')}
                    className="px-6 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors"
                >
                    ← Changer de template
                </button>
                <button
                    onClick={handlePreview}
                    className="flex items-center gap-2 px-8 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                >
                    <EyeIcon className="w-5 h-5" />
                    Aperçu du CV
                    <ArrowRightIcon className="w-4 h-4" />
                </button>
            </div>
        </div>
    );

    const renderStep = () => {
        switch (currentStep) {
            case 'template':
                return (
                    <TemplateSelector 
                        templates={templates}
                        onSelect={handleTemplateSelect}
                    />
                );
            case 'quick-edit':
                return renderQuickEdit();
            case 'preview':
                return (
                    <CvPreview
                        cvData={cvData}
                        cvStyle={cvStyle}
                        template={selectedTemplate}
                        onBack={() => setCurrentStep('quick-edit')}
                        onExport={handleExport}
                        isLoading={isLoading}
                        isEmbedded={false}
                    />
                );
            default:
                return null;
        }
    };

    const getStepIndicator = () => {
        const steps = [
            { key: 'template', label: 'Template', icon: DocumentTextIcon },
            { key: 'quick-edit', label: 'Remplir', icon: UserIcon },
            { key: 'preview', label: 'Aperçu', icon: EyeIcon },
        ];

        const currentStepIndex = steps.findIndex(step => step.key === currentStep);

        return (
            <div className="flex items-center justify-center mb-8">
                <div className="flex items-center space-x-4">
                    {steps.map((step, index) => {
                        const isActive = step.key === currentStep;
                        const isCompleted = index < currentStepIndex;
                        
                        return (
                            <div key={step.key} className="flex items-center">
                                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                                    isActive 
                                        ? 'bg-blue-600 border-blue-600 text-white' 
                                        : isCompleted 
                                            ? 'bg-green-500 border-green-500 text-white'
                                            : 'bg-gray-100 border-gray-300 text-gray-500'
                                }`}>
                                    {isCompleted ? (
                                        <CheckCircleIcon className="w-5 h-5" />
                                    ) : (
                                        <step.icon className="w-5 h-5" />
                                    )}
                                </div>
                                <span className={`ml-2 text-sm font-medium ${
                                    isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                                }`}>
                                    {step.label}
                                </span>
                                {index < steps.length - 1 && (
                                    <div className={`w-8 h-0.5 mx-2 ${
                                        isCompleted ? 'bg-green-500' : 'bg-gray-300'
                                    }`} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 pt-20 p-4 overflow-x-hidden">
            <div className="max-w-7xl mx-auto min-w-0">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center mb-4">
                        <SparklesIcon className="w-8 h-8 text-blue-600 mr-3" />
                        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                            Créateur de CV Simplifié
                        </h1>
                    </div>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto break-words mb-4">
                        Créez un CV professionnel en 5 minutes avec notre interface simplifiée
                    </p>
                    
                    {/* Indicateur de sauvegarde */}
                    <div className="flex items-center justify-center gap-2 text-sm">
                        {isSaving ? (
                            <div className="flex items-center gap-2 text-blue-600">
                                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Sauvegarde en cours...
                            </div>
                        ) : hasUnsavedChanges ? (
                            <div className="flex items-center gap-2 text-orange-600">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                                Modifications non sauvegardées
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-green-600">
                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                                Toutes les modifications sont sauvegardées
                            </div>
                        )}
                    </div>
                    
                    {/* Boutons d'action */}
                    <div className="flex gap-3 justify-center">
                        <button
                            onClick={handleLoadExistingData}
                            disabled={isLoading}
                            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                        >
                            {isLoading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Chargement...
                                </>
                            ) : (
                                <>
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                    </svg>
                                    Charger mes données
                                </>
                            )}
                        </button>
                        
                        {hasUnsavedChanges && (
                            <button
                                onClick={() => saveCvDataToServer(cvData, cvStyle, selectedTemplate)}
                                disabled={isSaving}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                            >
                                {isSaving ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sauvegarde...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                        </svg>
                                        Sauvegarder maintenant
                                    </>
                                )}
                            </button>
                        )}
                        
                    </div>
                </div>

                {/* Step Indicator */}
                {getStepIndicator()}

                {/* Main Content */}
                <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden max-w-full min-w-0">
                    {renderStep()}
                </div>
            </div>

        </div>
    );
};

export default CvGeneratorSimplified;
